import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { User, UserRole } from '../models/user.model';
import { ApiResponse } from '../models/api-response.model';
import {
  LoginData,
  RegisterRequest,
  normaliseRole,
} from '../models/auth.model';
import { environment } from '../../../environments/environment';

const TOKEN_KEY = 'jf_token';

// JWT claim URIs (.NET identity claim schema)
const CLAIM_ROLE =
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
const CLAIM_EMAIL =
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress';
const CLAIM_NAMEID =
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly _currentUser = signal<User | null>(null);
  readonly currentUser = this._currentUser.asReadonly();

  constructor(private http: HttpClient) {}

  /** POST /api/Auth/login → stores accessToken, sets current user from the response */
  login(email: string, password: string): Observable<LoginData> {
    return this.http
      .post<
        ApiResponse<LoginData>
      >(`${environment.apiUrl}/Auth/login`, { email, password })
      .pipe(
        map((res) => res.data),
        tap((data) => {
          if (data.accessToken)
            localStorage.setItem(TOKEN_KEY, data.accessToken);
          this._currentUser.set(this.toUser(data));
        }),
      );
  }

  /** POST /api/Auth/register — returns the server message envelope */
  register(payload: RegisterRequest): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${environment.apiUrl}/Auth/register`,
      payload,
    );
  }

  /** GET /api/Auth/me — current authenticated user profile */
  getMe(): Observable<User> {
    return this.http
      .get<ApiResponse<LoginData>>(`${environment.apiUrl}/Auth/me`)
      .pipe(
        map((res) => this.toUser(res.data)),
        tap((user) => this._currentUser.set(user)),
      );
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    this._currentUser.set(null);
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  /** Authenticated = a non-expired token is present */
  isAuthenticated(): boolean {
    return !this.isTokenExpired(this.getToken());
  }

  /**
   * Guarantees a current user is available when a valid token exists.
   * Used by route guards so a page refresh restores the session from the
   * JWT (role + identity claims) without needing a /me round-trip.
   * Returns false when there is no valid session.
   */
  ensureSession(): boolean {
    if (this._currentUser()) return true;
    const token = this.getToken();
    if (this.isTokenExpired(token)) {
      if (token) this.logout(); // expired — clear it
      return false;
    }
    const user = this.userFromToken(token!);
    if (!user) return false;
    this._currentUser.set(user);
    return true;
  }

  /** Build our internal User shape from the login/me payload */
  private toUser(d: LoginData): User {
    const role = normaliseRole(d.role);
    // Prefer a real name from the API; fall back to the email local-part for now.
    const name = d.name || d.fullName || d.email?.split('@')[0] || '';
    const words = name
      .replace(/[^a-zA-Z ]/g, '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    const initials = (
      words.length >= 2
        ? words[0][0] + words[1][0] // "John Makinde" → JM
        : (words[0] ?? 'U').slice(0, 2)
    ).toUpperCase();
    return {
      id: d.userId,
      name,
      email: d.email,
      role,
      initials,
      phone: d.phoneNumber,
      address: d.address,
      profileId: d.profileId ?? undefined,
    };
  }

  // ── JWT helpers ───────────────────────────────────────

  private decodeToken(token: string): Record<string, any> | null {
    try {
      const payload = token.split('.')[1];
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decodeURIComponent(escape(json)));
    } catch {
      return null;
    }
  }

  private isTokenExpired(token: string | null): boolean {
    if (!token) return true;
    const claims = this.decodeToken(token);
    if (!claims?.['exp']) return true;
    return Date.now() >= claims['exp'] * 1000;
  }

  private userFromToken(token: string): User | null {
    const c = this.decodeToken(token);
    if (!c) return null;
    const email = c['email'] ?? c[CLAIM_EMAIL] ?? '';
    const id = c['sub'] ?? c[CLAIM_NAMEID] ?? '';
    const role: UserRole = normaliseRole(
      c[CLAIM_ROLE] ?? c['role'] ?? 'citizen',
    );
    const namePart = (email as string).split('@')[0] || 'User';
    return {
      id,
      name: namePart,
      email,
      role,
      initials: namePart.slice(0, 2).toUpperCase(),
    };
  }
}
